/**
 * Telling "the tab is running an old build" apart from everything else.
 *
 * Every deploy publishes its own set of hashed chunks and drops the previous
 * one, so a tab that has been open across a deploy is holding a chunk manifest
 * that no longer exists on the CDN. Nothing breaks until it lazy-loads — a
 * route, a modal, the viewer — and gets a 404 back. Sessions here stay open for
 * hours, so this is not an edge case.
 *
 * Two signals, both handled by `plugins/10-app-update.client.ts`:
 * the build id published at `/build-info.json`, polled while the tab lives, and
 * the import error itself, for the deploy that lands between two polls.
 */

/** Written into the deploy by the `compiled` nitro hook in `nuxt.config.ts`. */
export const BUILD_INFO_PATH = "/build-info.json";

export interface BuildInfo {
  /** Netlify deploy id, commit ref, or a build timestamp. */
  buildId: string;
  builtAt?: string;
}

/**
 * The strings browsers use when a module fetch fails. Deliberately broad — the
 * wording differs per engine and per failure (404, wrong MIME type because the
 * SPA fallback answered with HTML, a preload that never resolved) and a missed
 * match leaves the user staring at a dead screen.
 */
const CHUNK_ERROR_PATTERNS = [
  "failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "importing a module script failed",
  "unable to preload css",
  "expected a javascript module script",
  "loading chunk",
  "loading css chunk",
  "chunkloaderror",
];

export const parseBuildInfo = (payload: unknown): BuildInfo | null => {
  const buildId = (payload as BuildInfo)?.buildId;
  if (typeof buildId !== "string" || buildId.length === 0) return null;

  const builtAt = (payload as BuildInfo)?.builtAt;

  return typeof builtAt === "string" ? { buildId, builtAt } : { buildId };
};

/**
 * Only an id we actually have on both sides counts as stale. A missing or
 * unreadable one means "we don't know", and guessing would nag the user into
 * reloading a session for nothing.
 */
export const isStaleBuild = (
  runningBuildId: string | undefined,
  deployedBuildId: string | undefined,
): boolean =>
  Boolean(runningBuildId) &&
  Boolean(deployedBuildId) &&
  runningBuildId !== deployedBuildId;

export const isChunkLoadError = (error: unknown): boolean => {
  const message = [
    (error as Error)?.message,
    (error as Error)?.name,
    typeof error === "string" ? error : "",
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (!message) return false;

  return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
};

/**
 * Reads the deployed build id.
 *
 * `cache: "no-store"` plus a unique query string because both the browser and
 * any proxy in front of it would otherwise be free to answer this from the same
 * cache that is serving the stale HTML we are trying to detect.
 *
 * @param fetcher injected by the tests; defaults to the global fetch
 * @param now injected by the tests; supplies the cache-busting value
 */
export const fetchBuildInfo = async (
  fetcher: typeof fetch = globalThis.fetch,
  now: () => number = Date.now,
): Promise<BuildInfo | null> => {
  try {
    const response = await fetcher(`${BUILD_INFO_PATH}?t=${now()}`, {
      cache: "no-store",
      headers: { accept: "application/json" },
    });

    if (!response.ok) return null;

    return parseBuildInfo(await response.json());
  } catch {
    // Offline, or the file is not there (local dev, a host that didn't get the
    // build hook). Never a reason to surface anything to the user.
    return null;
  }
};
